/**
 * Trial-expiry prompts (FR-BILLING-03, AC-BILL-003).
 *
 * Two prompts, one component, because they are one escalation:
 *
 *   | Time to expiry | Prompt                                            |
 *   | -------------- | ------------------------------------------------- |
 *   | > 3 days       | nothing                                           |
 *   | 3 days → 1 day | sticky amber banner, dismissible for the session  |
 *   | ≤ 1 day        | modal, no backdrop/Escape/X — a button is required |
 *
 * The modal is deliberately hard to dismiss: the two ways out are "Upgrade now"
 * (card capture) and "Continue on Free" (an explicit acknowledgement that the
 * team is about to lose Varsity features). It is therefore hand-rolled rather
 * than built on `OverlayDialog`, which dismisses on backdrop click and Escape.
 *
 * Both dismissals are per tab (sessionStorage): a coach who acknowledges is not
 * nagged again this session, and the prompt returns on the next sign-in while
 * the trial is still expiring.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { STORAGE_KEYS } from '../../lib/constants';
import { readSessionFlag, writeSessionFlag } from '../../lib/session-storage';

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;
/** Advance-notice window (FR-BILLING-03): 3 days out. */
export const TRIAL_BANNER_WINDOW_DAYS = 3;
/** Day-of window: inside 24h the modal replaces the banner. */
export const TRIAL_MODAL_WINDOW_HOURS = 24;

export type TrialPromptStage = 'none' | 'advance' | 'day-of';

/** Whole days left, rounded up: 30 remaining hours reads as "2 days". */
export function trialDaysRemaining(trialExpiresAt: string, nowMs: number): number {
  const expiresMs = Date.parse(trialExpiresAt);
  if (Number.isNaN(expiresMs)) return 0;
  return Math.max(0, Math.ceil((expiresMs - nowMs) / DAY_MS));
}

/**
 * Which prompt is due. An already-elapsed `trial_expires_at` still returns
 * `day-of`: if the hourly downgrade job is late, the user must still be told.
 */
export function trialPromptStage(trialExpiresAt: string, nowMs: number): TrialPromptStage {
  const expiresMs = Date.parse(trialExpiresAt);
  if (Number.isNaN(expiresMs)) return 'none';
  const hoursLeft = (expiresMs - nowMs) / HOUR_MS;
  if (hoursLeft <= TRIAL_MODAL_WINDOW_HOURS) return 'day-of';
  if (hoursLeft <= TRIAL_BANNER_WINDOW_DAYS * 24) return 'advance';
  return 'none';
}

function formatExpiryDate(trialExpiresAt: string): string {
  const parsed = new Date(trialExpiresAt);
  if (Number.isNaN(parsed.getTime())) return 'your trial end date';
  return parsed.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export interface TrialBannerProps {
  /** ISO 8601 `trial_expires_at` from `GET /billing/v1/subscriptions/{id}`. */
  trialExpiresAt: string;
  /** Trial tier display name, e.g. `Varsity`. */
  planName: string;
  /** Tier the trial falls back to, e.g. `Free`. */
  downgradesToPlanName: string;
  /** Features lost at expiry — derived from the plan catalog by the caller. */
  lostFeatures: readonly string[];
  /** Launches checkout for the trial tier. */
  onUpgrade: () => void;
  /** Jump the user to the plan comparison (banner CTA on a non-plan tab). */
  onComparePlans?: () => void;
  /** Injectable clock for tests; defaults to `Date.now()`. */
  nowMs?: number;
}

export function TrialBanner({
  trialExpiresAt,
  planName,
  downgradesToPlanName,
  lostFeatures,
  onUpgrade,
  onComparePlans,
  nowMs,
}: TrialBannerProps): JSX.Element | null {
  const now = nowMs ?? Date.now();
  const stage = trialPromptStage(trialExpiresAt, now);
  const daysLeft = trialDaysRemaining(trialExpiresAt, now);

  const [isBannerDismissed, setIsBannerDismissed] = useState(() =>
    readSessionFlag(STORAGE_KEYS.trialBannerDismissed)
  );
  const [isModalAcknowledged, setIsModalAcknowledged] = useState(() =>
    readSessionFlag(STORAGE_KEYS.trialModalAcknowledged)
  );
  const modalRef = useRef<HTMLDivElement | null>(null);
  const primaryRef = useRef<HTMLButtonElement | null>(null);

  const isModalOpen = stage === 'day-of' && !isModalAcknowledged;

  const acknowledge = useCallback((): void => {
    writeSessionFlag(STORAGE_KEYS.trialModalAcknowledged, true);
    setIsModalAcknowledged(true);
  }, []);

  // Focus management: the primary action takes focus on open and Tab cycles
  // inside the modal. Escape is intentionally NOT wired — dismissal requires a
  // decision, so there is no accidental way past the acknowledgement.
  useEffect(() => {
    if (!isModalOpen) return undefined;
    primaryRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return;
      const panel = modalRef.current;
      if (panel === null) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>('button:not([disabled])'));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isModalOpen]);

  if (stage === 'none') return null;

  if (isModalOpen) {
    return (
      <div className="trial-modal__backdrop">
        <div
          className="trial-modal"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="trial-modal-title"
          aria-describedby="trial-modal-body"
          ref={modalRef}
        >
          <h2 className="trial-modal__title" id="trial-modal-title">
            Your {planName} trial expires today
          </h2>
          <div className="trial-modal__body" id="trial-modal-body">
            <p>
              After {formatExpiryDate(trialExpiresAt)} you will lose access to the features below.
              Your data is preserved on the {downgradesToPlanName} tier — nothing is deleted.
            </p>
            {lostFeatures.length === 0 ? null : (
              <ul className="trial-modal__features">
                {lostFeatures.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            )}
          </div>
          <div className="btn-row trial-modal__actions">
            <button type="button" className="btn btn--secondary" onClick={acknowledge}>
              Continue on {downgradesToPlanName}
            </button>
            <button type="button" className="btn btn--primary" ref={primaryRef} onClick={onUpgrade}>
              Upgrade now
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Day-of, already acknowledged: keep a quiet reminder in place of the modal
  // so the state is still visible without nagging.
  if (stage === 'day-of') {
    return (
      <div className="banner banner--warning trial-banner" role="status">
        <p>
          Your {planName} trial ends today. You chose to continue on {downgradesToPlanName} — you
          can upgrade at any time.
        </p>
        <div className="btn-row">
          <button type="button" className="btn btn--sm btn--primary" onClick={onUpgrade}>
            Upgrade now
          </button>
        </div>
      </div>
    );
  }

  if (isBannerDismissed) return null;

  return (
    <div className="banner banner--warning trial-banner" role="status">
      <div className="trial-banner__copy">
        <p className="banner__title">
          Your {planName} trial expires in {daysLeft} {daysLeft === 1 ? 'day' : 'days'}
        </p>
        <p>
          Upgrade before {formatExpiryDate(trialExpiresAt)} to keep{' '}
          {lostFeatures.length === 0 ? 'your current features' : lostFeatures.join(', ')}. Without
          an upgrade the team moves to {downgradesToPlanName} and keeps read-only access to all
          existing data.
        </p>
      </div>
      <div className="btn-row trial-banner__actions">
        <button
          type="button"
          className="btn btn--sm btn--secondary"
          onClick={() => {
            writeSessionFlag(STORAGE_KEYS.trialBannerDismissed, true);
            setIsBannerDismissed(true);
          }}
        >
          Dismiss
        </button>
        <button
          type="button"
          className="btn btn--sm btn--primary"
          onClick={onComparePlans ?? onUpgrade}
        >
          Upgrade now
        </button>
      </div>
    </div>
  );
}
